import { Flame, ShieldHalf, Ambulance, Headset, Medal, Dog } from "lucide-react";
import { IMAGES, SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Photo } from "@/components/site/Photo";
import { Reveal, SectionHeading, CtaLink, DonateButton, Pending } from "@/components/site/Primitives";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { SERVICE_DOG_FIELDS } from "@/components/forms/formDefs";
import { SERVICE_DOG_FAQ } from "@/content/content";
import { MeetLuna } from "@/components/site/MeetLuna";
import { FloridaDisclosure } from "@/components/site/FloridaDisclosure";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PRINCIPLES = [
  { t: "Who we serve", d: "Active and retired firefighters, law enforcement officers, EMS personnel, dispatchers, and military veterans." },
  { t: "Service area", d: "Applicants living in Palm Beach and Broward counties." },
  { t: "Program cost", d: "Free to approved participants." },
  { t: "Participant responsibilities", d: "Food, routine veterinary care, and other ongoing expenses of dog ownership." },
];

const SERVE = [
  { icon: Flame, t: "Firefighters", d: "Active and retired firefighters who have faced demanding and difficult incidents in service to their communities." },
  { icon: ShieldHalf, t: "Law Enforcement Officers", d: "Active and retired officers and deputies from patrol, investigations, and specialized units." },
  { icon: Ambulance, t: "EMS Personnel", d: "Active and retired paramedics and EMTs who provide care in critical moments." },
  { icon: Headset, t: "Dispatchers", d: "Active and retired telecommunicators, the first voice on the line in an emergency." },
  { icon: Medal, t: "Military Veterans", d: "Men and women who have served in the United States Armed Forces." },
];

const STEPS = [
  { t: "Submit an Application", d: "Applicants submit their applications through the First Responders Foundation website." },
  { t: "Foundation Review", d: "First Responders Foundation reviews each application for completeness and basic eligibility." },
  { t: "Referral to Big Dog Ranch Rescue", d: "Applications that meet the initial requirements are referred to Big Dog Ranch Rescue." },
  { t: "Approval Decision", d: "Big Dog Ranch Rescue makes the final decision regarding applicant approval." },
  { t: "Selecting a Potential Dog", d: "Approved participants may select a potential dog with assistance from Big Dog Ranch Rescue staff." },
  { t: "Evaluation, Training, Placement, and Follow-Up", d: "Big Dog Ranch Rescue evaluates and approves dogs and oversees training, placement, and follow-up." },
];

const DOG_TYPES = [
  { t: "Service Dog", d: "Under the Americans with Disabilities Act (ADA), a service dog is individually trained to do work or perform tasks for a person with a disability. The tasks must be directly related to that person's disability.", tag: "Task-trained" },
  { t: "Therapy Dog", d: "Therapy dogs visit settings such as hospitals, schools, or stations to provide comfort to many people. They are not service animals under the ADA and do not carry the same public access rights.", tag: "Comfort for groups" },
  { t: "Emotional Support Animal", d: "An emotional support animal provides comfort through its presence and is not trained to perform specific disability-related tasks. ESAs are not service animals under the ADA.", tag: "Companionship" },
  { t: "Station Dog", d: "A station dog lives at or regularly visits a fire, police, or dispatch facility to support the team as a whole. A station dog is not an individual's service dog.", tag: "Supports a crew" },
];

const ServeCards = () => (
  <section className="py-24 md:py-32" data-testid="sdp-who-we-serve-section">
    <div className="container-x">
      <SectionHeading eyebrow="Who We Serve" title="Built for Those Who Serve." intro="The program is open to active and retired first responders and military veterans living in Palm Beach and Broward counties. Big Dog Ranch Rescue makes the final decision regarding applicant approval." />
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {SERVE.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.07} className="group relative flex flex-col border border-navy/10 bg-white p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold" data-testid={`sdp-serve-card-${i + 1}`}>
            <s.icon className="text-navy transition-colors duration-500 group-hover:text-gold-deep" size={28} />
            <h3 className="mt-10 text-lg font-bold">{s.t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/75">{s.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="grain relative bg-navy py-24 text-paper md:py-32" data-testid="sdp-process-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
      <div className="lg:sticky lg:top-32 lg:self-start">
        <SectionHeading light eyebrow="How the Program Works" title="Six Steps, Handled With Care." intro="First Responders Foundation is your starting point. Big Dog Ranch Rescue makes approval decisions and oversees every dog's evaluation, training, placement, and follow-up." />
      </div>
      <ol className="relative border-l border-gold/30">
        {STEPS.map((s, i) => (
          <Reveal as="li" key={s.t} className="relative pb-14 pl-10 last:pb-0 md:pl-14" data-testid={`sdp-step-${i + 1}`}>
            <span className="absolute -left-[21px] top-0 flex h-10 w-10 items-center justify-center rounded-full border border-gold bg-navy font-display text-sm font-bold text-gold">{i + 1}</span>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Step {i + 1}</p>
            <h3 className="mt-2 text-2xl font-bold text-paper md:text-3xl">{s.t}</h3>
            <p className="mt-4 max-w-xl leading-relaxed text-[#C9D4DF]">{s.d}</p>
            {s.pending && <div className="mt-4"><Pending dark label={s.pending} /></div>}
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const OwnDog = () => (
  <section className="py-24 md:py-32" data-testid="sdp-own-dog-section">
    <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-24">
      <Photo image={IMAGES.lunaResting} ratio="aspect-[5/4]" />
      <div>
        <SectionHeading eyebrow="Applicants With a Dog" title="Already Have a Dog?" />
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-ink/85 md:text-lg">
            Applicants may request an evaluation of their existing dog. Big Dog Ranch Rescue must evaluate and approve the dog before it can enter the training program. Approval is not guaranteed.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-ink/70">Answer "Yes" to the dog question on the application and share a few details about your dog.</p>
          <div className="mt-10"><CtaLink to="#apply" testId="sdp-own-dog-apply-button">Start Your Application</CtaLink></div>
        </Reveal>
      </div>
    </div>
  </section>
);

const DogTypes = () => (
  <section className="bg-mist py-24 md:py-32" data-testid="sdp-dog-types-section">
    <div className="container-x">
      <SectionHeading eyebrow="Understanding the Difference" title="Not Every Working Dog Is a Service Dog." intro="These terms are often used interchangeably, but they describe different roles with different responsibilities and legal standing." />
      <div className="mt-14 grid gap-px bg-navy/15 md:grid-cols-2 lg:grid-cols-4">
        {DOG_TYPES.map((d, i) => (
          <Reveal key={d.t} delay={i * 0.08} className="flex flex-col bg-paper p-8" data-testid={`sdp-dog-type-${i + 1}`}>
            <Dog className={i === 0 ? "text-gold-deep" : "text-navy/50"} size={26} />
            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate">{d.tag}</p>
            <h3 className="mt-2 text-xl font-bold">{d.t}</h3>
            <p className="mt-4 text-sm leading-relaxed text-ink/80">{d.d}</p>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 max-w-3xl text-xs leading-relaxed text-slate">
        This information is general and is not legal advice. Participation in the Foundation's program does not by itself establish ADA eligibility or public access rights. Applicable federal, state, and local laws, including housing rules, may treat these animals differently.
      </p>
    </div>
  </section>
);

const Apply = () => (
  <section id="apply" className="py-24 md:py-32" data-testid="sdp-apply-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <SectionHeading eyebrow="Application" title="Apply for the Program." intro="Start here. First Responders Foundation reviews every application for completeness and basic eligibility, then refers qualifying applications to Big Dog Ranch Rescue for a final decision." />
        <Reveal delay={0.1} className="mt-8 space-y-4 border-l-2 border-gold bg-white p-6 text-sm leading-relaxed text-ink/80">
          <p>Please do not include diagnoses, medical history, Social Security numbers, or other sensitive clinical information.</p>
          <p>With your authorization, the Foundation shares your application information with Big Dog Ranch Rescue for program evaluation. See our <a href="/privacy-policy" className="font-semibold text-navy underline underline-offset-2">Privacy Policy</a>.</p>
          <p>Submitting an application does not guarantee approval, placement, completion of training, or service dog certification.</p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="border border-navy/10 bg-white p-6 shadow-[0_12px_40px_rgba(16,38,63,0.08)] md:p-10">
        <FormBuilder
          formType="service-dog"
          fields={SERVICE_DOG_FIELDS}
          testId="service-dog-form"
          submitLabel="Submit Application"
          successTitle="Thank you. Your application has been received."
          successText="First Responders Foundation will review your application for completeness and basic eligibility and contact you regarding next steps. Applications that meet the initial requirements are referred to Big Dog Ranch Rescue, which makes the final approval decision."
          footnote="Your information is shared only with authorized Foundation staff and, with your authorization, Big Dog Ranch Rescue. It is never sold."
        />
      </Reveal>
    </div>
  </section>
);

const Faq = () => (
  <section className="border-t border-navy/10 bg-white py-24 md:py-32" data-testid="sdp-faq-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <SectionHeading eyebrow="Program FAQ" title="Questions, Answered Honestly." className="lg:sticky lg:top-32 lg:self-start" />
      <Accordion type="single" collapsible className="border-t border-navy/15">
        {SERVICE_DOG_FAQ.map((f, i) => (
          <AccordionItem key={f.q} value={`q${i}`} className="border-navy/15" data-testid={`faq-accordion-item-${i + 1}`}>
            <AccordionTrigger className="py-6 text-left font-display text-base font-bold text-navy hover:no-underline md:text-lg" data-testid={`faq-trigger-${i + 1}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="pb-6 text-base leading-relaxed text-ink/80">
              {f.a}
              {f.pending && <div className="mt-3"><Pending label={f.pending} /></div>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export default function ServiceDog() {
  return (
    <>
      <Seo title="Service Dog Program for First Responders & Veterans | Palm Beach & Broward" description="Free service dog program for approved active and retired first responders and veterans in Palm Beach and Broward counties, in partnership with Big Dog Ranch Rescue." path="/service-dog-program" />
      <PageHero
        eyebrow="Service Dog Program"
        lines={[{ text: "Service Dogs." }, { text: "Stronger Connections." }, { text: "Renewed Hope.", className: "text-gold" }]}
        intro="In partnership with Big Dog Ranch Rescue, our Service Dog Program supports approved active and retired first responders and military veterans in Palm Beach and Broward counties. The program is free to approved participants."
        image={IMAGES.luna}
      >
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <CtaLink to="#apply" variant="btn-gold" testId="sdp-hero-apply-button">Apply for the Program</CtaLink>
          <DonateButton label="Donate to the Program" href={SITE.donateLinks.serviceDog} testId="sdp-hero-donate-button" />
        </div>
      </PageHero>
      <section className="py-24 md:py-32" data-testid="sdp-mission-section">
        <div className="container-x grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHeading eyebrow="Our Partnership" title="In Partnership With Big Dog Ranch Rescue." intro="First Responders Foundation works with Big Dog Ranch Rescue to connect those who serve with carefully evaluated, professionally trained dogs. The Foundation receives and reviews applications; Big Dog Ranch Rescue makes the final approval decision, evaluates and approves each dog, and oversees training, placement, and follow-up." />
            <Reveal delay={0.1} className="mt-8 border-l-2 border-gold pl-5 text-base leading-relaxed text-ink/85" data-testid="sdp-bdrr-partner">
              Our service dog evaluation and training partner:{" "}
              <a href="https://www.bdrr.org/" target="_blank" rel="noopener noreferrer" className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-deep" data-testid="sdp-bdrr-link">
                Big Dog Ranch Rescue<span className="sr-only"> (opens in a new tab)</span>
              </a>
            </Reveal>
            <div className="mt-12 grid gap-px bg-navy/10 sm:grid-cols-2">
              {PRINCIPLES.map((p) => (
                <Reveal key={p.t} className="bg-paper p-6">
                  <h3 className="text-base font-bold">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/75">{p.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="relative">
            <Photo image={IMAGES.luna} ratio="aspect-[4/5]" testId="sdp-luna-photo" />
            <Reveal className="relative -mt-16 ml-6 mr-6 bg-navy p-7 text-paper md:-mt-24 md:ml-12 md:mr-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Foundation Ambassador</p>
              <p className="mt-2 font-display text-2xl font-extrabold">Meet Luna</p>
              <p className="mt-3 text-sm leading-relaxed text-[#C9D4DF]">Adopted from Big Dog Ranch Rescue, Luna is a quick learner who loves people and other dogs.</p>
              <a href="#meet-luna" className="link-underline mt-4 inline-block text-sm font-semibold text-gold" data-testid="sdp-meet-luna-link">Read Luna's story</a>
            </Reveal>
          </div>
        </div>
      </section>
      <MeetLuna />
      <ServeCards />
      <Steps />
      <OwnDog />
      <DogTypes />
      <Apply />
      <Faq />
      <section className="bg-navy py-20 text-paper" data-testid="sdp-donate-section">
        <div className="container-x flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-2xl text-2xl font-extrabold text-paper md:text-4xl">Help make the next partnership possible.</h2>
          <DonateButton label="Donate Now" href={SITE.donateLinks.serviceDog} testId="sdp-footer-donate-button" />
        </div>
        <div className="container-x"><FloridaDisclosure dark className="mt-10 max-w-4xl" testId="sdp-florida-disclosure" /></div>
      </section>
    </>
  );
}
